import { env } from '@/lib/env'
import { CreateGameData, GameAccessType, GameLevel, Game, GameModality, ParticipationStatus, ParticipantRole, ChargeType, Participant } from '@/types/game'
import { BaseMeiucaService } from './MeiucaService'
import { formatDateTimeForAPI, parseAPIDate, mapDateFiltersToAPI } from '@meiuca/web-foundation'

// Mapeamento dos enums para os valores da API
const mapGameAccessTypeToAPI = (accessType: GameAccessType): string => {
  return accessType // Já está no formato correto: 'public', 'linkOnly', 'private'
}

const mapGameLevelToAPI = (level: GameLevel): string => {
  // Agora o enum já tem os valores corretos da API
  return level
}

const mapModalityToAPI = (modality: string): string => {
  // A API espera 'soccer' para futebol, mas no enum é FOOTBALL
  switch (modality.toUpperCase()) {
    case 'FOOTBALL':
      return 'soccer'
    case 'BASKETBALL':
      return 'basketball'
    case 'VOLLEYBALL':
      return 'volleyball'
    case 'TENNIS':
      return 'tennis'
    case 'FUTSAL':
      return 'futsal'
    case 'BEACH_TENNIS':
      return 'beach_tennis'
    case 'PADEL':
      return 'padel'
    case 'RUNNING':
      return 'running'
    default:
      return modality.toLowerCase()
  }
}

const mapPaymentModeToAPI = (chargeType: string): 'split_equal' | 'fixed_value' => {
  switch (chargeType) {
    case 'dividido':
      return 'split_equal'
    case 'valor_fixo':
      return 'fixed_value'
    default:
      return 'fixed_value'
  }
}

const mapPaymentModeFromAPI = (paymentMode: string): ChargeType => {
  switch (paymentMode) {
    case 'split_equal':
      return ChargeType.DIVIDED
    case 'fixed_value':
      return ChargeType.FIXED
    default:
      return ChargeType.FIXED
  }
}

const mapParticipantRoleFromAPI = (role: string): ParticipantRole => {
  switch (role) {
    case 'owner':
      return ParticipantRole.OWNER
    case 'admin':
      return ParticipantRole.ADMIN
    case 'player':
      return ParticipantRole.PLAYER
    default:
      return ParticipantRole.PLAYER
  }
}

const mapParticipationStatusFromAPI = (status: string): ParticipationStatus => {
  switch (status) {
    case 'invited':
      return ParticipationStatus.INVITED
    case 'confirmed':
      return ParticipationStatus.CONFIRMED
    case 'canceled':
      return ParticipationStatus.CANCELED
    case 'declined':
      return ParticipationStatus.DECLINED
    default:
      return ParticipationStatus.INVITED
  }
}

// Removido - agora usa o helper centralizado em dateFormatters.ts

// Mapeia dados da API para o formato do frontend
const mapAPIGameToFrontend = (apiGame: GameItemAPI): Game => {
  console.log('📅 Mapping game dates:', { 
    id: apiGame.id, 
    startTime: apiGame.startTime, 
    endTime: apiGame.endTime 
  })
  
  // Parse seguro das datas ISO com tratamento robusto usando helper centralizado
  let startDate: Date
  let endDate: Date
  
  try {
    startDate = parseAPIDate(apiGame.startTime)
    endDate = parseAPIDate(apiGame.endTime)
    
  } catch (error) {
    console.error('❌ Error parsing dates:', error)
    startDate = new Date() // Fallback para data atual
    endDate = new Date()
  }
  
  console.log('🕒 Parsed dates:', { 
    startDate: startDate.toString(),
    endDate: endDate.toString(),
    startDateValid: !isNaN(startDate.getTime()),
    endDateValid: !isNaN(endDate.getTime())
  })
  
  // Verifica se as datas são válidas
  if (isNaN(startDate.getTime())) {
    console.error('❌ Invalid startTime:', apiGame.startTime)
  }
  if (isNaN(endDate.getTime())) {
    console.error('❌ Invalid endTime:', apiGame.endTime)
  }
  
  // Mapeia modalidade da API para enum do frontend
  const mapModalityFromAPI = (modality: string): GameModality => {
    switch (modality.toLowerCase()) {
      case 'soccer': return GameModality.FOOTBALL
      case 'basketball': return GameModality.BASKETBALL
      case 'volleyball': return GameModality.VOLLEYBALL
      case 'tennis': return GameModality.TENNIS
      case 'futsal': return GameModality.FUTSAL
      case 'beach_tennis': return GameModality.BEACH_TENNIS
      case 'padel': return GameModality.PADEL
      case 'running': return GameModality.RUNNING
      default: return GameModality.FOOTBALL
    }
  }

  // Mapeia nível da API para enum do frontend
  const mapLevelFromAPI = (level: string): GameLevel => {
    // Agora o enum já tem os valores corretos da API, então pode fazer cast direto
    const levelValue = level.toLowerCase()
    switch (levelValue) {
      case 'beginner': return GameLevel.BEGINNER
      case 'intermediate': return GameLevel.INTERMEDIATE
      case 'advanced': return GameLevel.ADVANCED
      case 'opened': return GameLevel.OPEN
      default: return GameLevel.OPEN
    }
  }

  console.log('✅ Final mapped game dates:', {
    id: apiGame.id,
    originalStartTime: apiGame.startTime,
    parsedStartDate: startDate.toString(),
    parsedEndDate: endDate.toString(),
    startDateValid: !isNaN(startDate.getTime()),
    endDateValid: !isNaN(endDate.getTime())
  })

  const mappedModality = mapModalityFromAPI(apiGame.modality)
  const mappedLevel = mapLevelFromAPI(apiGame.level)
  const gameName = apiGame.name || 'Jogo'

  // Mapeia accessType da API para enum do frontend
  const mapAccessTypeFromAPI = (accessType: GameAccessTypeAPI): GameAccessType => {
    switch (accessType.type.toLowerCase()) {
      case 'public': return GameAccessType.PUBLIC
      case 'linkonly': return GameAccessType.LINK_ONLY
      case 'private': return GameAccessType.PRIVATE
      default: return GameAccessType.PUBLIC
    }
  }

  return {
    id: apiGame.id,
    name: gameName,
    modality: mappedModality,
    startDate: startDate,
    endDate: endDate,
    location: apiGame.location.name,
    address: apiGame.location.address,
    price: apiGame.amount / 100, // Convert cents to reais
    paymentMode: mapPaymentModeFromAPI(apiGame.paymentMode),
    occupiedSlots: apiGame.confirmedCount || 0, // Use confirmed count from API
    totalSlots: apiGame.maxParticipants,
    level: mappedLevel,
    accessType: mapAccessTypeFromAPI(apiGame.accessType),
    accessToken: apiGame.accessType.token || undefined,
    userStatus: apiGame.viewer ? mapParticipationStatusFromAPI(apiGame.viewer.status) : ParticipationStatus.NOT_PARTICIPANT,
    userRole: apiGame.viewer ? mapParticipantRoleFromAPI(apiGame.viewer.role) : ParticipantRole.PLAYER,
  }
}

interface CreateGameAPIRequest {
  name: string
  startTime: string
  endTime: string
  accessType: string
  level: string
  minParticipants: number
  maxParticipants: number
  paymentMode: 'split_equal' | 'fixed_value'
  amount: number // cents
  modality: string
  location: {
    address: string
    name: string
    googlePlaceID?: string | null
    coordinate: {
      latitude: number
      longitude: number
    }
  }
}

interface CreateGameAPIResponse {
  data: {
    modality: string
    level: string
    link: string
    accessType: {
      token: string
      type: string
    }
    startTime: string
    id: string
    status: string
    endTime: string
  }
}

// Enums e interfaces para listagem de jogos
export enum GameStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  COMPLETED = 'completed'
}

// Interface para filtros da API
interface GameFiltersAPI {
  status?: GameStatus[]
  modality?: string[]
  level?: string[]
  startDate?: string // ISO string
  endDate?: string // ISO string
  query?: string // Busca por texto (nome, local, endereço, etc.)
  lat?: number // Latitude para busca por proximidade
  lng?: number // Longitude para busca por proximidade
}

// Converte filtros do frontend para API
const mapFiltersToAPI = (filters: any): GameFiltersAPI => {
  const apiFilters: GameFiltersAPI = {}
  
  // Adiciona query de busca por texto se existir
  if (filters.query && filters.query.trim()) {
    apiFilters.query = filters.query.trim()
  }
  
  // Adiciona coordenadas para busca por proximidade
  console.log('🗺️ Verificando coordenadas no mapFiltersToAPI:', {
    hasLatitude: !!filters.latitude,
    hasLongitude: !!filters.longitude,
    latitude: filters.latitude,
    longitude: filters.longitude
  })
  
  if (filters.latitude && filters.longitude) {
    apiFilters.lat = filters.latitude
    apiFilters.lng = filters.longitude
    console.log('✅ Coordenadas adicionadas aos apiFilters')
  } else {
    console.log('❌ Coordenadas não foram adicionadas - missing:', {
      latitude: filters.latitude,
      longitude: filters.longitude
    })
  }
  
  if (filters.modalidade && filters.modalidade.length > 0) {
    apiFilters.modality = filters.modalidade.map((m: string) => {
      // Mapear labels em português para valores da API
      switch (m) {
        case 'Futebol': return 'soccer'
        case 'Basquete': return 'basketball'
        case 'Vôlei': return 'volleyball'
        case 'Tênis': return 'tennis'
        case 'Futsal': return 'futsal'
        case 'Beach Tennis': return 'beach_tennis'
        case 'Padel': return 'padel'
        case 'Corrida': return 'running'
        default: return m.toLowerCase()
      }
    })
  }
  
  if (filters.nivel && filters.nivel.length > 0) {
    apiFilters.level = filters.nivel.map((n: string) => {
      // Mapear labels em português para valores da API
      switch (n) {
        case 'Iniciante': return 'beginner'
        case 'Intermediário': return 'intermediate'
        case 'Avançado': return 'advanced'
        case 'Aberto': return 'opened'
        default: return 'opened'
      }
    })
  }
  
  // Processa filtros de data usando helper centralizado
  const dateFilters = mapDateFiltersToAPI(filters)
  if (dateFilters.startDate) {
    apiFilters.startDate = dateFilters.startDate
  }
  if (dateFilters.endDate) {
    apiFilters.endDate = dateFilters.endDate
  }
  
  return apiFilters
}

interface GameLocation {
  name: string
  coordinate: {
    latitude: number
    longitude: number
  }
  address: string
}

interface GameAccessTypeAPI {
  type: string
  token: string
}

interface GameItemAPI {
  id: string
  name: string
  startTime: string
  endTime: string
  accessType: GameAccessTypeAPI
  level: string
  minParticipants: number
  maxParticipants: number
  paymentMode: 'split_equal' | 'fixed_value'
  amount: number // cents
  modality: string
  status: string
  location: GameLocation
  confirmedCount?: number // Number of confirmed participants
  viewer?: {
    role: 'admin' | 'player'
    status: 'invited' | 'confirmed' | 'canceled' | 'declined'
  }
}

interface GetGamesAPIResponse {
  data: GameItemAPI[]
}

interface GetGameDetailsAPIResponse {
  data: GameItemAPI
}

interface ParticipantAPI {
  id: string
  name: string
  nickname?: string
  userId?: string
  gameId: string
  status: 'invited' | 'confirmed' | 'declined' | 'canceled'
  role: 'owner' | 'admin' | 'player'
}

interface GetGameParticipantsAPIResponse {
  data: ParticipantAPI[]
}

export class GameService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: env.API_URL + '/auth/refresh',
      cookiePrefix: '', // No prefix for meiuca-app (keeps original behavior)
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        // Use replace to prevent back button issues - redirect to root
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }
  async getMyGames(status?: GameStatus[], filters?: any): Promise<Game[]> {
    try {
      console.log('📋 Fetching games...', status ? `with status: ${status}` : 'all statuses')

      // Monta os parâmetros da API
      const apiFilters: GameFiltersAPI = {}
      
      if (status && status.length > 0) {
        apiFilters.status = status
      }
      
      if (filters) {
        const mappedFilters = mapFiltersToAPI(filters)
        Object.assign(apiFilters, mappedFilters)
      }

      console.log('🔍 API Filters:', apiFilters)

      const response = await this.get('/me/games', { params: apiFilters })
      
      console.log('✅ Games fetched successfully:', response.data)
      
      const apiResponse = response.data as GetGamesAPIResponse
      const games = apiResponse.data.map(mapAPIGameToFrontend)
      
      console.log('🎮 Games converted to frontend format:', games)
      return games

    } catch (error) {
      throw error
    }
  }

  async searchGames(status?: GameStatus[], filters?: any): Promise<Game[]> {
    try {
      console.log('🔍 Searching games...', status ? `with status: ${status}` : 'all statuses')

      // Monta os parâmetros da API (mesma lógica do getMyGames)
      const apiFilters: GameFiltersAPI = {}
      
      if (status && status.length > 0) {
        apiFilters.status = status
      }
      
      if (filters) {
        const mappedFilters = mapFiltersToAPI(filters)
        Object.assign(apiFilters, mappedFilters)
      }

      console.log('🔍 API Filters for search:', apiFilters)

      // Usa endpoint /games ao invés de /me/games para buscar todos os jogos
      const response = await this.get('/games', { params: apiFilters })
      
      console.log('✅ Games search results:', response.data)
      
      const apiResponse = response.data as GetGamesAPIResponse
      const games = apiResponse.data.map(mapAPIGameToFrontend)
      
      console.log('🎮 Search games converted to frontend format:', games)
      return games

    } catch (error) {
      throw error
    }
  }

  async getGameById(gameId: string, token?: string): Promise<Game> {
    try {
      console.log(`🔍 Fetching game details for ID: ${gameId}`)
      
      // Headers serão adicionados automaticamente pela MeiucaService

      // Preparar URL com token de acesso se fornecido
      let url = `/games/${gameId}`
      if (token) {
        url += `?token=${encodeURIComponent(token)}`
      }

      console.log(`📡 API URL: ${env.API_URL}${url}`)

      const response = await this.get<GetGameDetailsAPIResponse>(url)
      
      console.log('✅ Game details fetched successfully:', response.data)
      
      // A resposta tem o objeto do jogo dentro de data (não é um array)
      const apiGame = response.data.data
      return mapAPIGameToFrontend(apiGame)

    } catch (error) {
      throw error
    }
  }

  async createGame(gameData: CreateGameData): Promise<CreateGameAPIResponse> {
    try {

      // Mapeia os dados do formulário para o formato da API
      const apiRequest: CreateGameAPIRequest = {
        name: gameData.name,
        startTime: formatDateTimeForAPI(gameData.startTime),
        endTime: formatDateTimeForAPI(gameData.endTime),
        accessType: mapGameAccessTypeToAPI(gameData.accessType),
        level: mapGameLevelToAPI(gameData.level),
        modality: mapModalityToAPI(gameData.modality),
        minParticipants: gameData.minParticipants,
        maxParticipants: gameData.maxParticipants,
        paymentMode: mapPaymentModeToAPI(gameData.charge.type),
        amount: Math.round(gameData.charge.amount * 100), // Convert reais to cents
        location: {
          address: gameData.location.address,
          name: gameData.location.name,
          googlePlaceID: gameData.location.googlePlaceID,
          coordinate: {
            latitude: gameData.location.coordinate.latitude,
            longitude: gameData.location.coordinate.longitude
          }
        }
      }

      console.log('🎯 Creating game with data:', apiRequest)
      console.log('📡 API URL:', `${env.API_URL}/games`)

      const response = await this.post('/games', apiRequest)
      
      console.log('✅ Game created successfully:', response.data)
      return response.data as CreateGameAPIResponse

    } catch (error) {
      throw error
    }
  }

  // Mapeia participante da API para frontend
  private mapAPIParticipantToFrontend(apiParticipant: ParticipantAPI): Participant {
    return {
      id: apiParticipant.id,
      name: apiParticipant.name,
      nickname: apiParticipant.nickname,
      userId: apiParticipant.userId,
      gameId: apiParticipant.gameId,
      status: mapParticipationStatusFromAPI(apiParticipant.status),
      role: mapParticipantRoleFromAPI(apiParticipant.role),
      avatar: undefined // TODO: Add when available in API
    }
  }

  // Busca participantes do jogo
  async getGameParticipants(gameId: string): Promise<Participant[]> {
    try {
      console.log(`🔍 Fetching game participants for ID: ${gameId}`)
      
      // Headers serão adicionados automaticamente pela MeiucaService

      const url = `/games/${gameId}/players`
      console.log(`📡 API URL: ${env.API_URL}${url}`)

      const response = await this.get<GetGameParticipantsAPIResponse>(url)
      
      console.log('✅ Game participants fetched successfully:', response.data)
      
      // Mapear participantes da API para frontend
      const participants = response.data.data.map(this.mapAPIParticipantToFrontend)
      return participants

    } catch (error) {
      throw error
    }
  }

  // Convida usuário para o jogo
  async inviteUserToGame(gameId: string, userId: string): Promise<void> {
    try {
      console.log(`👥 Inviting user ${userId} to game ${gameId}`)
      
      const url = `/games/${gameId}/players/invite`
      const payload = { userId }
      
      await this.post(url, payload)
      
      console.log('✅ User invited successfully')
      
    } catch (error) {
      throw error
    }
  }

  // Solicita participação no jogo (para usuários não convidados)
  async joinGame(gameId: string): Promise<void> {
    try {
      console.log(`🎯 Requesting to join game ${gameId}`)
      
      const url = `/games/${gameId}/players/join`
      
      await this.post(url, {})
      
      console.log('✅ Join request sent successfully')
      
    } catch (error) {
      throw error
    }
  }

  // Confirma a participação do usuário no jogo (para usuários já convidados)
  async confirmParticipation(gameId: string): Promise<void> {
    try {
      console.log(`✅ Confirming participation in game ${gameId}`)
      
      const url = `/games/${gameId}/players/accept`
      
      await this.patch(url)
      
      console.log('✅ Participation confirmed successfully')
      
    } catch (error) {
      throw error
    }
  }

  // Cancela a participação do usuário no jogo
  async cancelParticipation(gameId: string): Promise<void> {
    try {
      console.log(`🚫 Canceling participation in game ${gameId}`)
      
      const url = `/games/${gameId}/players/cancel`
      
      await this.patch(url)
      
      console.log('🚫 Participation canceled successfully')
      
    } catch (error) {
      throw error
    }
  }

  // Gerenciar participante (aprovar/rejeitar/remover)
  async manageParticipant(gameId: string, participantId: string, action: 'approve' | 'reject' | 'remove'): Promise<void> {
    try {
      console.log(`🛠️ Managing participant ${participantId} in game ${gameId} with action: ${action}`)
      
      let url: string
      let method: 'patch' | 'delete'
      
      switch (action) {
        case 'approve':
          url = `/games/${gameId}/players/${participantId}/confirm`
          method = 'patch'
          break
        case 'reject':
          url = `/games/${gameId}/players/${participantId}/cancel`
          method = 'patch'
          break
        case 'remove':
          url = `/games/${gameId}/players/${participantId}`
          method = 'delete'
          break
        default:
          throw new Error(`Ação inválida: ${action}`)
      }
      
      if (method === 'patch') {
        await this.patch(url)
      } else {
        await this.delete(url)
      }
      
      console.log(`✅ Participant ${action}d successfully`)
      
    } catch (error) {
      throw error
    }
  }

  // Convida guest (pessoa sem conta) para o jogo
  async inviteGuestToGame(gameId: string, name: string, phoneNumber?: string): Promise<void> {
    try {
      console.log(`👤 Inviting guest "${name}" to game ${gameId}`)
      
      const url = `/games/${gameId}/players/guest`
      const payload: { name: string; phoneNumber?: string } = { name }
      
      if (phoneNumber) {
        payload.phoneNumber = phoneNumber
      }
      
      await this.post(url, payload)
      
      console.log('✅ Guest invited successfully')
      
    } catch (error) {
      throw error
    }
  }
}

// Export singleton instance
export const gameService = new GameService()
